/**
 * IndexedDB 上传缓存工具类 (Dexie.js 实现)
 * 用于存储和管理 AWS S3 断点续传的缓存数据
 * 支持 LRU 缓存清理策略
 */

const Dexie = require('dexie').default || require('dexie');
const ResourceLockManager = require('./resource-lock-manager');
const logger = require('../logger');

/**
 * Dexie 数据库类定义
 */
class UploadCacheDB extends Dexie {
    constructor() {
        super('UmuUploadCache');

        // v2: 原有的 schema (兼容旧数据)
        this.version(2).stores({
            uploads: 'uploadId, [teacherId+fileMD5], createdAt',
        });

        // v3: 新增 lastAccessedAt 字段用于 LRU
        this.version(3)
            .stores({
                uploads: 'uploadId, [teacherId+fileMD5], createdAt, lastAccessedAt',
            })
            .upgrade((tx) => {
                // 迁移旧数据：为所有记录添加 lastAccessedAt
                return tx
                    .table('uploads')
                    .toCollection()
                    .modify((record) => {
                        if (!record.lastAccessedAt) {
                            record.lastAccessedAt = record.createdAt || Date.now();
                        }
                    });
            });
    }
}

class UploadCache {
    static DB_NAME = 'UmuUploadCache';
    static DB_VERSION = 3;
    static STORE_NAME = 'uploads';
    static MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
    static CLEAN_TARGET_RATIO = 0.8; // 清理到80%
    static CLEAN_CHECK_INTERVAL = 60 * 1000; // 60秒检查一次

    static db = null;
    static isSupported = null;
    static lastCleanCheckTime = 0; // 上次清理检查时间

    /**
     * 检查 IndexedDB 是否被支持
     */
    static checkSupport() {
        if (this.isSupported !== null) {
            return this.isSupported;
        }

        // 检查是否在浏览器环境且支持 IndexedDB
        if (typeof window === 'undefined' || !window.indexedDB) {
            this.isSupported = false;
            return false;
        }

        this.isSupported = true;
        return true;
    }

    /**
     * 初始化 IndexedDB 数据库
     */
    static async init() {
        if (!this.checkSupport()) {
            throw new Error('IndexedDB is not supported');
        }

        if (this.db) {
            logger.log('[UploadCache] DB already initialized, skipping...');
            return;
        }

        try {
            this.db = new UploadCacheDB();
            await this.db.open();

            // 初始化完成后清理过期记录
            const deletedCount = await this.cleanExpiredRecords();
            if (deletedCount > 0) {
                logger.log(`[UploadCache] Cleaned ${deletedCount} expired records`);
            }
        } catch (error) {
            throw new Error(`IndexedDB init failed: ${error.message}`);
        }
    }

    /**
     * 获取数据库实例
     */
    static async getDB() {
        if (!this.db) {
            await this.init();
        }

        if (!this.db) {
            throw new Error('IndexedDB not initialized');
        }

        return this.db;
    }

    /**
     * 计算记录的大小(字节)
     * 使用 Blob 获取更精确的大小估算
     */
    static calculateRecordSize(record) {
        try {
            const blob = new Blob([JSON.stringify(record)]);
            return blob.size;
        } catch (error) {
            // 降级到原有计算方式
            return JSON.stringify(record).length * 2;
        }
    }

    /**
     * 检查并清理缓存 (LRU 策略)
     * 基于 lastAccessedAt 删除最少访问的记录
     * @returns {Promise<{needClean: boolean, deletedCount: number, totalSize: number}>}
     */
    static async checkAndCleanLRU() {
        const db = await this.getDB();

        // 1. 获取所有记录，按 lastAccessedAt 升序排序（最少访问的在前）
        const allRecords = await db.uploads.orderBy('lastAccessedAt').toArray();

        // 2. 计算总大小
        let totalSize = 0;
        const recordInfos = allRecords.map((record) => {
            const size = this.calculateRecordSize(record);
            totalSize += size;
            return {
                uploadId: record.uploadId,
                size: size,
            };
        });

        // 3. 判断是否需要清理
        if (totalSize <= this.MAX_CACHE_SIZE) {
            return { needClean: false, deletedCount: 0, totalSize };
        }

        // 4. 执行清理：删除最少访问的记录直到达到目标大小
        const targetSize = this.MAX_CACHE_SIZE * this.CLEAN_TARGET_RATIO;
        let currentSize = totalSize;
        const toDelete = [];

        for (const info of recordInfos) {
            if (currentSize <= targetSize) break;
            toDelete.push(info.uploadId);
            currentSize -= info.size;
        }

        // 5. 批量删除
        if (toDelete.length > 0) {
            await db.uploads.bulkDelete(toDelete);
        }

        return {
            needClean: true,
            deletedCount: toDelete.length,
            totalSize,
        };
    }

    /**
     * 保存上传记录
     * 自动设置 lastAccessedAt（LRU 写操作）
     */
    static async saveRecord(record) {
        const db = await this.getDB();

        // 设置访问时间
        record.lastAccessedAt = Date.now();
        if (!record.createdAt) {
            record.createdAt = record.lastAccessedAt;
        }

        // 保存记录
        await db.uploads.put(record);

        // 节流检查：避免频繁触发清理
        const now = Date.now();
        if (now - this.lastCleanCheckTime < this.CLEAN_CHECK_INTERVAL) {
            return;
        }
        this.lastCleanCheckTime = now;

        await this.checkAndCleanLRU();
    }

    /**
     * 根据 teacherId 和 fileMD5 查找记录
     * 不更新访问时间（性能优化）
     */
    static async findRecord(teacherId, fileMD5) {
        const db = await this.getDB();

        try {
            const record = await db.uploads.where(['teacherId', 'fileMD5']).equals([teacherId, fileMD5]).first();

            return record || null;
        } catch (error) {
            throw new Error(`Find record failed: ${error.message}`);
        }
    }

    /**
     * 根据 uploadId 获取记录
     * 不更新访问时间（性能优化）
     */
    static async getRecord(uploadId) {
        const db = await this.getDB();

        try {
            const record = await db.uploads.get(uploadId);
            return record || null;
        } catch (error) {
            throw new Error(`Get record failed: ${error.message}`);
        }
    }

    /**
     * 更新分片信息（带资源锁的单事务原子操作）
     * 更新 lastAccessedAt（LRU 写操作）
     */
    static async updatePart(uploadId, part) {
        // 使用资源锁保证同一 uploadId 的更新串行执行
        return ResourceLockManager.executeExclusive(uploadId, () => this._updatePartInternal(uploadId, part));
    }

    /**
     * 内部更新方法（单事务原子操作）
     * @private
     */
    static async _updatePartInternal(uploadId, part) {
        const db = await this.getDB();

        try {
            await db.transaction('rw', db.uploads, async () => {
                // 1. 获取记录
                const record = await db.uploads.get(uploadId);

                if (!record) {
                    throw new Error(`Upload record not found: ${uploadId}`);
                }

                // 2. 初始化 parts 数组（如果不存在）
                if (!record.parts) {
                    record.parts = [];
                }

                // 3. 查找现有分片或添加新分片
                const existingPartIndex = record.parts.findIndex((p) => p.PartNumber === part.PartNumber);

                if (existingPartIndex >= 0) {
                    record.parts[existingPartIndex] = part;
                } else {
                    record.parts.push(part);
                }

                // 4. 按分片序号排序
                record.parts.sort((a, b) => a.PartNumber - b.PartNumber);

                // 5. 更新访问时间（LRU）
                record.lastAccessedAt = Date.now();

                // 6. 写回数据库
                await db.uploads.put(record);
            });

            return true;
        } catch (error) {
            throw new Error(`Update part failed: ${error.message}`);
        }
    }

    /**
     * 删除记录
     */
    static async deleteRecord(uploadId) {
        const db = await this.getDB();

        try {
            await db.uploads.delete(uploadId);
            return true;
        } catch (error) {
            throw new Error(`Delete record failed: ${error.message}`);
        }
    }

    /**
     * 根据 teacherId 和 fileMD5 删除记录
     */
    static async deleteRecordByTeacherIdAndFileMD5(teacherId, fileMD5) {
        const db = await this.getDB();

        try {
            // 先通过索引查找记录
            const record = await db.uploads.where(['teacherId', 'fileMD5']).equals([teacherId, fileMD5]).first();

            // 如果记录不存在，直接返回
            if (!record) {
                return false;
            }

            // 使用主键删除记录
            await db.uploads.delete(record.uploadId);
            return true;
        } catch (error) {
            throw new Error(`Delete record failed: ${error.message}`);
        }
    }

    /**
     * 清理过期记录
     * @param maxAge 最大缓存时间(毫秒)，默认7天
     */
    static async cleanExpiredRecords(maxAge = 7 * 24 * 60 * 60 * 1000) {
        const db = await this.getDB();
        const cutoffTime = Date.now() - maxAge;

        try {
            // 使用 Dexie 的 where + delete 清理过期记录
            const deletedCount = await db.uploads.where('createdAt').below(cutoffTime).delete();

            return deletedCount;
        } catch (error) {
            throw new Error(`Clean expired records failed: ${error.message}`);
        }
    }

    /**
     * 关闭数据库连接
     */
    static close() {
        if (this.db) {
            this.db.close();
            this.db = null;
        }
    }
}

module.exports = { UploadCache };
